import type {
  ClientEntitlements,
  Permissions,
} from '@nursio-link/common/authorization';
import {redirect} from 'react-router-dom';

import {type AuthUser, getCurrentUser, getGroups} from '@/services/authService';
import type {UserGroup} from '@/services/graphqlHooks';

import isCurrentClientEntitle from './isCurrentClientEntitled';
import isCurrentGroupsAuthorized from './isCurrentGroupsAuthorized';
import {isCurrentUserCompliant} from './isCurrentUserCompliant';

export const protectedLoader =
  (clientEntitlements: ClientEntitlements, permissions: Permissions) =>
  async () => {
    const isAuthorized = await isCurrentGroupsAuthorized(permissions);

    if (!isAuthorized) {
      return redirect('/unauthorized');
    }

    const isEntitled = await isCurrentClientEntitle(clientEntitlements);

    if (!isEntitled) {
      return redirect('/not-entitled');
    }

    return null;
  };

export type AuthenticatedRootLoaderData = {
  user: AuthUser;
  groups: UserGroup[];
};

export const authenticatedRootLoader = async (): Promise<
  AuthenticatedRootLoaderData | Response
> => {
  const user = await getCurrentUser();

  if (!user) {
    return redirect('/login');
  }

  const groups = await getGroups();

  if (!groups.length) {
    return redirect('/unauthorized');
  }

  const {isTermApproved, isOwnerTermApproved, isPrivacyApproved, isOwner} =
    await isCurrentUserCompliant();

  if (!isOwner && !isOwnerTermApproved) {
    return redirect('/owner-approval-required');
  }

  if (!isTermApproved) {
    return redirect('/terms-and-conditions');
  }

  if (!isPrivacyApproved) {
    return redirect('/privacy-policy');
  }

  return {user, groups};
};

export function isLoaderRedirect(
  result: AuthenticatedRootLoaderData | Response
): result is Response {
  return result instanceof Response;
}
